import {
  EventItem,
  StatusHistoryColorMode,
  StatusHistoryLabelGrouping,
  StatusHistoryPanelPropsBase,
  StatusHistoryPanelSpec,
} from '@/core';
import { BasePanelProps, PropToSpecMapping } from '@/plugin-system';

/**
 * Types for StatusHistory that are only used in the `Viz` variant.
 */
interface StatusHistoryPropsVizOnly {
  /**
   * Click handler for selected event
   */
  onEventClick?: (e: EventItem) => void;
  /**
   * Override the sync group used for shared crosshair interactions.
   * By default, the sync group is derived from the app metadata context.
   * Set to a custom string to use a specific group, or `false` to opt out of syncing.
   */
  syncGroup?: string | false;
}

export interface StatusHistoryProps extends BasePanelProps, StatusHistoryPanelPropsBase, StatusHistoryPropsVizOnly {
  /** mapSpecToProps delivers these as snake_case at runtime. */
  color_mode?: StatusHistoryColorMode;
  /** mapSpecToProps delivers these as snake_case at runtime. */
  label_grouping?: StatusHistoryLabelGrouping;
}

// TODO: can convert to camelCase here later if desired but leaving snake_case since nested label_grouping
// properties are snake_case and deep prop to spec mapping is awkward.
export const STATUS_HISTORY_PROP_TO_SPEC_MAPPING: PropToSpecMapping<StatusHistoryProps, StatusHistoryPanelSpec> = {
  color_mode: 'color_mode',
  label_grouping: 'label_grouping',
  thresholds: 'thresholds',
  unit: 'unit',
  visual: 'visual',
};

export const ROW_LABEL_WIDTH = 100;
export const ROW_SPACING = 8; // MUI spacing(1) = 8px, used between label and cells
export const AXIS_PLUS_LEGEND_HEIGHT = 65;
export const MAX_LABEL_WIDTH_FRACTION = 1 / 3;
export const LABEL_PADDING = 16;
export const ESTIMATED_CHAR_WIDTH_PX = 7;

/**
 * Convert a timestamp to a pixel-X coordinate within the status history chart area
 * (excluding label and spacing). Returns `undefined` if the timestamp is outside the grid.
 */
export const STATUS_CELL_BORDER_WIDTH = 1;

export function statusHistoryTimestampToPixelX(
  ts: number,
  startMs: number,
  stepMs: number | undefined,
  numCols: number,
  chartWidth: number
): number | undefined {
  if (!stepMs || stepMs <= 0 || numCols <= 0 || chartWidth <= 0) return undefined;
  const colIdx = Math.round((ts - startMs) / stepMs);
  if (colIdx < 0 || colIdx >= numCols) return undefined;
  const colWidth = chartWidth / numCols;
  return colIdx * colWidth + (colWidth - STATUS_CELL_BORDER_WIDTH) / 2;
}
